import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { LoadingContext } from "../LoadingContext";
import Loading from "./Loading";
import "./AdminInquiries.css";
import Swal from "sweetalert2";
import { toLocalDateString } from "../dateUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const MySwal = Swal.mixin({
    customClass: {
        container: "my-swal-container",
    },
});

function AdminInquiries() {
    const getTodayDate = () => {
        const today = new Date();
        return toLocalDateString(today);
    };

    const { authFetch, userInfo } = useContext(AuthContext);
    const [inquiries, setInquiries] = useState([]);
    const [filteredInquiries, setFilteredInquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [inquiriesPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const { setIsLoading } = useContext(LoadingContext);
    const navigate = useNavigate();


    useEffect(() => {
        const styleEl = document.createElement("style");
        styleEl.textContent = `
      .my-swal-container {
        z-index: 20000 !important;
      }
    `;
        document.head.appendChild(styleEl);
        return () => {
            document.head.removeChild(styleEl);
        };
    }, []);

    const fetchInquiries = async () => {
        setIsLoading(true);
        setLoading(true);
        try {
            const response = await authFetch(
                `${API_BASE_URL}/Users/customer-users?search=`
            );

            if (!response.ok) {
                if (response.status === 404) {
                    setInquiries([]);
                    setFilteredInquiries([]);
                    setLoading(false);
                    setIsLoading(false);
                    return;
                } else {
                    throw new Error('Failed to fetch inquiries');
                }
            }

            const data = await response.json();

            if (!data || data.length === 0) {
                setInquiries([]);
                setFilteredInquiries([]);
                setLoading(false);
                setIsLoading(false);
                return;
            }

            const sortedInquiries = data.sort((a, b) => {
                return new Date(b.submittedDate) - new Date(a.submittedDate);
            });

            // Apply client-side search filter
            let result = sortedInquiries;
            if (searchTerm) {
                const search = searchTerm.toLowerCase();
                result = result.filter(
                    (inquiry) =>
                        (inquiry.name && inquiry.name.toLowerCase().includes(search)) ||
                        (inquiry.companyName &&
                            inquiry.companyName.toLowerCase().includes(search))
                );
            }

            setInquiries(sortedInquiries);
            setFilteredInquiries(result);
        } catch (err) {
            setInquiries([]);
            setFilteredInquiries([]);
        } finally {
            setIsLoading(false);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInquiries();
    }, [setIsLoading]);

    useEffect(() => {
        let result = [...inquiries];

        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            result = result.filter(
                (inquiry) =>
                    (inquiry.email && inquiry.email.toLowerCase().includes(search)) ||
                    (inquiry.phoneNumber &&
                        inquiry.phoneNumber.toLowerCase().includes(search))
            );
        }

        setFilteredInquiries(result);
        setCurrentPage(1);
    }, [inquiries]);

    const indexOfLastInquiry = currentPage * inquiriesPerPage;
    const indexOfFirstInquiry = indexOfLastInquiry - inquiriesPerPage;
    const currentInquiries = filteredInquiries.slice(
        indexOfFirstInquiry,
        indexOfLastInquiry
    );
    const totalPages = Math.ceil(filteredInquiries.length / inquiriesPerPage);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const applyFilters = () => {
        fetchInquiries();
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);

            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();

            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');

            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`; 
        } catch (e) {
            return dateString;
        }
    };

    const handleCsvExport = () => {

        try {

            // Must match rowData below exactly - the leading No. column had no header,
            // which shifted every value one column to the right in Excel.
            var csvContent = "No.,Username,Email,First Name,Last Name,Phone,Point,Registration Date\n";

            const escapeCSV = (value) => {
                if (value === null || value === undefined) return '';
                const str = String(value);
                if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            };

            inquiries.forEach((item, index) => {

                const rowData = [
                    index + 1,
                    escapeCSV(item.username),
                    escapeCSV(item.email),
                    escapeCSV(item.firstName),
                    escapeCSV(item.lastName),
                    escapeCSV(item.phoneNumber),
                    escapeCSV(item.points),
                    escapeCSV(formatDate(item.createdAt)),
                ];

                csvContent += rowData.join(',') + "\n";
            });

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });

            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `Ardency_Customer_${toLocalDateString(new Date())}.csv`;

            document.body.appendChild(link);

            // Trigger the download
            link.click();

            // Clean up
            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 100);
        } catch (error) {
            MySwal.fire({
                title: "Error!",
                text: "Failed to export CSV. Please try again.",
                icon: "error",
                confirmButtonColor: "#45a049",
            });
        }
    };


    if (loading) {
        return (
            <div className="admin-inquiry-container">
                <div className="admin-inquiry-loading">
                    <Loading />
                </div>
            </div>
        );
    }

    return (
        <div className="admin-inquiry-container">
            {/* Back to Dashboard Button */}
            <div className="admin-inquiry-header">
                <button
                    className="admin-inquiry-back-btn"
                    onClick={() => navigate("/admin/dashboard")}
                >
                    <i className="fas fa-arrow-left"></i> Back to Dashboard
                </button>
            </div>

            {/* Title Section */}
            <div className="admin-inquiry-title">
                <h1>Customer</h1>
            </div>

            {/* Filter Section */}
            <div className="admin-inquiry-filter">
                <div className="admin-inquiry-filter-group">
                    <label>Search</label>
                    <input
                        type="text"
                        placeholder="Search by email or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <button className="admin-inquiry-filter-btn" onClick={applyFilters}>
                    Search
                </button>
                <button className="admin-inquiry-filter-btn" onClick={handleCsvExport}>
                    Export Excel
                </button>
            </div>

            {/* Table Container Section */}
            <div className="admin-inquiry-table-container">
                {currentInquiries.length > 0 ? (
                    <table className="admin-inquiry-table">
                        <thead>
                            <tr>
                                <th>Username</th>
                                <th>Email</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Phone</th>
                                <th>Point</th>
                                <th>Registration Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentInquiries.map((user) => (
                                <tr key={user.id}>
                                    <td>{user.username}</td>
                                    <td>{user.email}</td>
                                    <td>{user.firstName}</td>
                                    <td>{user.lastName}</td>
                                    <td>{user.phoneNumber || "N/A"}</td>
                                    <td>{user.point || 0}</td>
                                    <td>{formatDate(user.createdAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="admin-inquiry-empty">
                        <i className="fas fa-question-circle"></i>
                        <p className="primary-message">
                            No users found matching your criteria.
                        </p>
                        <p className="secondary-message">
                            The default filters will reflect the new status and current day's data. To view
                            all users, please adjust the filters and click apply.
                        </p>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {filteredInquiries.length > inquiriesPerPage && (
                <div className="admin-inquiry-pagination">
                    <button
                        className="admin-inquiry-pagination-btn"
                        onClick={() => paginate(1)}
                        disabled={currentPage === 1}
                    >
                        <i className="fas fa-angle-double-left"></i>
                    </button>
                    <button
                        className="admin-inquiry-pagination-btn"
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        <i className="fas fa-angle-left"></i>
                    </button>

                    {[...Array(totalPages).keys()].map((number) => {
                        if (
                            number + 1 === 1 ||
                            number + 1 === totalPages ||
                            (number + 1 >= currentPage - 1 && number + 1 <= currentPage + 1)
                        ) {
                            return (
                                <button
                                    key={number + 1}
                                    className={`admin-inquiry-pagination-btn ${currentPage === number + 1 ? "active" : ""
                                        }`}
                                    onClick={() => paginate(number + 1)}
                                >
                                    {number + 1}
                                </button>
                            );
                        }

                        if (
                            (number + 1 === currentPage - 2 && currentPage > 3) ||
                            (number + 1 === currentPage + 2 && currentPage < totalPages - 2)
                        ) {
                            return (
                                <span key={number + 1} className="admin-inquiry-pagination-btn">
                                    ...
                                </span>
                            );
                        }
                        return null;
                    })}

                    <button
                        className="admin-inquiry-pagination-btn"
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="fas fa-angle-right"></i>
                    </button>
                    <button
                        className="admin-inquiry-pagination-btn"
                        onClick={() => paginate(totalPages)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="fas fa-angle-double-right"></i>
                    </button>
                </div>

            )}


        </div>
    );
}

export default AdminInquiries;
